import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { Provider } from "react-redux";
import { BrowserRouter } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import App from "./App";
import Loader from "./components/loader";
import { sentryConfig } from "./config/sentry";
import "./i18n";
import "./index.css";
import { store } from "./store/store";
import { getActiveCountry, getAppVersion, getEnvironmentType } from "./utils/utils";
import { ENV_TYPES } from "./types/env.types";
import AwsConfigFetcher from "./services/AwsConfigFetcher";
import { BrandIdentifier, CountryKeyManager } from "./utils/environmentManager";

// add current country as global variable
window.COUNTRY = getActiveCountry();
console.log("Current Country: ", window.COUNTRY);
const env = getEnvironmentType();

console.log("Environment: ", env);

const keyManager = CountryKeyManager.getInstance();
keyManager.initialize(window.COUNTRY);

window.COUNTRY = getActiveCountry();

// Determine which brand and add the class to body
const brand = keyManager.getBrand();
const brandClassMap: Record<BrandIdentifier, string> = {
  [BrandIdentifier.Flexistore]: "brand-flexistore",
  [BrandIdentifier.StorPro]: "brand-storpro",
};

const brandClass = brandClassMap[brand];
if (brandClass) {
  document.body.classList.add(brandClass);
} else {
  console.warn("Unknown brand:", brand);
}

console.log("Brand Identifier:", brand);

// Check if the current environment is production (where we want to disable logs)
if (env === ENV_TYPES.PRODUCTION) {
  // Override console.log to be a no-op in production
  console.log = () => {};
}

(async () => {
  // configure Sentry
  Sentry.init(sentryConfig);

  const { logger } = await import("./utils/sentryLogger");

  const configFetcher = AwsConfigFetcher;

  await configFetcher.getAwsConfig();

  await configFetcher.initializeAmplify();

  // Initialize Sentry logger
  try {
    await logger.initialize();
    logger.info("Application starting", {
      appVersion: getAppVersion(),
      country: getActiveCountry(),
      environment: env,
    });
  } catch (error) {
    console.error("Failed to initialize Sentry logger:", error);
  }


  // Set up axios interceptors
  const { setupAxiosInterceptors } = await import("./utils/axiosInterceptors");
  setupAxiosInterceptors();

  // Create a query client: react query
  const queryClient = new QueryClient();

  ReactDOM.createRoot(document.getElementById("root") as HTMLElement).render(
    <Suspense fallback={<Loader />}>
      <QueryClientProvider client={queryClient}>
        <Provider store={store}>
          <BrowserRouter>
            <App />
            <ToastContainer />
          </BrowserRouter>
        </Provider>

        {/*dev tools for react query*/}
        <ReactQueryDevtools />
      </QueryClientProvider>
    </Suspense>,
  );
})();
